<template>
  <ak-modal v-model:active="isModalActive" :flex-body="true">
    <ak-modal-body>
      <ak-modal-title>Reject Withdrawal Request</ak-modal-title>
      <ak-field label="Rejection Reason" required>
        <ak-input
          v-model="rejectionReason"
          :disabled="loading"
          type="textarea"
          minlength="10"
          maxlength="300"
          required
          rows="5"
        />
      </ak-field>
    </ak-modal-body>
    <ak-modal-footer>
      <ak-modal-button @click.prevent="isModalActive = false">
        Cancel
      </ak-modal-button>
      <ak-modal-button
        color="danger"
        :disabled="rejectionReason.length < 10 && !loading"
        @click.prevent="rejectWithdrawalTransaction"
      >
        Reject
      </ak-modal-button>
    </ak-modal-footer>
  </ak-modal>
</template>

<script>
import { ref, watch, defineComponent } from 'vue'
import ModalButton from '@akerolabs/akero-vue/src/components/modal/ModalButton.vue'
import ModalBody from '@akerolabs/akero-vue/src/components/modal/ModalBody.vue'
import ModalFooter from '@akerolabs/akero-vue/src/components/modal/ModalFooter.vue'
import Modal from '@akerolabs/akero-vue/src/components/modal/Modal.vue'
import ModalTitle from '@akerolabs/akero-vue/src/components/modal/ModalTitle.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src/components/toast'
import { request } from '@akerolabs/akero-vue/src/utils/request'

export default defineComponent({
  name: 'AkRejectRequestModal',
  components: {
    [ModalButton.name]: ModalButton,
    [Modal.name]: Modal,
    [ModalTitle.name]: ModalTitle,
    [Field.name]: Field,
    [Input.name]: Input,
    [ModalBody.name]: ModalBody,
    [ModalFooter.name]: ModalFooter,
  },
  props: {
    /**
     * @type {Boolean}
     * @description use to display and hide the modal
     */
    isActive: {
      type: Boolean,
      default: false,
    },
    /**
     * @type {String}
     * @description the current transaction id being used as a potential rejection
     */
    transactionId: {
      type: String,
      required: true,
    },
    /**
     * @type {Object}
     * @description the current user object
     */
    user: {
      type: Object,
      required: true,
    },
  },
  emits: ['update:isActive', 'rejected'],
  setup(props, { emit }) {
    const isModalActive = ref(props.isActive)
    const rejectionReason = ref('')
    const loading = ref(false)

    /**
     * @description Emits the update to the isActive prop and resets the rejection reason when modal is closed
     */
    watch(isModalActive, (value) => {
      emit('update:isActive', value)
      if (!value) {
        rejectionReason.value = ''
      }
    })

    /**
     * @description Updates the internal isModalActive based on the isActive prop
     */
    watch(
      () => props.isActive,
      (value) => {
        isModalActive.value = value
      },
    )

    /**
     * @description Makes api request to reject the selected transaction
     */
    const rejectWithdrawalTransaction = async () => {
      loading.value = true
      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to reject request`,
          type: 'danger',
        })
      }
      const displaySuccess = () => {
        ToastProgrammatic.open({
          message: 'Success request rejected',
          type: 'success',
        })
      }
      try {
        await request.post(
          `/survey/transactions/${props.transactionId}/reject`,
          {
            rejected_reason: rejectionReason.value,
            rejectedBy: props.user._id,
            rejectedByName: props.user.name,
          },
        )
        loading.value = false
        isModalActive.value = false
        emit('rejected')
        displaySuccess()
      } catch (ex) {
        displayError(ex)
      }
    }

    return {
      isModalActive,
      loading,
      rejectionReason,
      rejectWithdrawalTransaction,
    }
  },
})
</script>
